import {
  Badge,
  Button,
  Card,
  Combobox,
  ScrollArea,
  SegmentedControl,
  Table,
  useCombobox,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import type { Client } from "@/types";
import classes from "./TableScrollArea.module.css";
import cx from "clsx";
import "./InactiveClients.scss";
import { IconChevronDown } from "@tabler/icons-react";
import { optionStrings } from "@/constants.ts";
import { useClientNavigation } from "@utils/hooks/useClientNavigation.ts";

interface InactiveClientsProps {
  missingOrganizer: Client[];
  missingDocuments: Client[];
  missingIdentity: Client[];
  selectedItem: string;
  setSelectedItem: (value: string) => void;
}

const InactiveClients = ({
  missingOrganizer,
  missingDocuments,
  missingIdentity,
  selectedItem,
  setSelectedItem,
}: InactiveClientsProps) => {
  const { navigateToClient } = useClientNavigation();

  // Ensure default values for the props
  missingOrganizer = missingOrganizer || [];
  missingDocuments = missingDocuments || [];
  missingIdentity = missingIdentity || [];

  const allClientsArray = [
    ...missingOrganizer,
    ...missingDocuments,
    ...missingIdentity,
  ];

  // Remove duplicates
  const allClients = Array.from(
    new Map(allClientsArray.map((client) => [client.userId, client])).values()
  );

  const clientsToDisplay = {
    "Missing Organizer": missingOrganizer,
    "Missing Documents": missingDocuments,
    "Missing Identity Verification": missingIdentity,
    "All Clients": allClients,
  };
  const [scrolled, setScrolled] = useState(false);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = optionStrings.map((option, index) => (
    <Combobox.Option className="option-container" value={option} key={option}>
      <p
        className="option"
        style={{
          backgroundColor: "rgb(255, 218, 148)",
          color: "rgb(147, 95, 0)",
          textTransform: "lowercase",
          fontSize: "1rem",
          margin: index === 0 ? "0rem" : "1rem 0rem 0rem 0rem",
        }}
      >
        {option}
      </p>
    </Combobox.Option>
  ));

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<keyof typeof clientsToDisplay>(
    Object.keys(clientsToDisplay)[0] as keyof typeof clientsToDisplay
  );

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0;
    }
  }, [missingDocuments, missingOrganizer, missingIdentity, value]);

  const getInactiveClients = (clients?: Client[]) => {
    return clients?.map((client, index) => {
      const accountCreated = new Date(client.accountCreated.date);
      const localDateString = accountCreated.toLocaleDateString();

      let badges;
      if (
        missingOrganizer?.length > 0 ||
        missingDocuments?.length > 0 ||
        missingIdentity?.length > 0
      ) {
        badges = (
          <div className="badges">
            {missingOrganizer.some((c) => c.userId === client.userId) && (
              <Badge size="md" color="#5C59E8" variant="filled">
                Complete Organizer
              </Badge>
            )}
            {missingDocuments.some((c) => c.userId === client.userId) && (
              <Badge size="md" color="teal" variant="filled">
                Upload Documents
              </Badge>
            )}
            {missingIdentity.some((c) => c.userId === client.userId) && (
              <Badge size="md" color="#00AEEF" variant="filled">
                Verify Identity
              </Badge>
            )}
          </div>
        );
      } else {
        badges = <></>;
      }

      return (
        <Table.Tr
          key={`${client.userId}${index}`}
          className="table-row"
          onClick={() => navigateToClient(client)}
        >
          <Table.Td className="element">{`${client.firstName} ${client.lastName}`}</Table.Td>
          <Table.Td className="element hide-on-small-screens">
            {localDateString}
          </Table.Td>
          <Table.Td className="element">{badges}</Table.Td>
        </Table.Tr>
      );
    });
  };

  return (
    <>
      <div className="main">
        <Card className={`inactive-clients-card`}>
          <div className="title">
            <h2>Inactive Clients</h2>

            <div className="age-container">
              <h2>Account Age</h2>
              <Combobox
                store={combobox}
                width={250}
                position="bottom-start"
                withArrow
                onOptionSubmit={(val) => {
                  setSelectedItem(val);
                  combobox.closeDropdown();
                }}
              >
                <Combobox.Target>
                  <Button
                    className="combobox-target-btn"
                    onClick={() => combobox.toggleDropdown()}
                    rightSection={<IconChevronDown />}
                  >
                    {selectedItem}
                  </Button>
                </Combobox.Target>

                <Combobox.Dropdown className="dropdown-container">
                  <Combobox.Options className="combobox-options">
                    {options}
                  </Combobox.Options>
                </Combobox.Dropdown>
              </Combobox>
            </div>
          </div>
          <p className="inactive-info-text">
            Showing all of your client accounts older than{" "}
            <span>{selectedItem}</span> with status of Onboarding
          </p>
          <div className="control-container">
            <SegmentedControl
              size="md"
              color="#0077CC"
              radius="xl"
              data={Object.keys(clientsToDisplay)}
              onChange={(value) =>
                setValue(value as keyof typeof clientsToDisplay)
              }
              withItemsBorders={false}
            />
          </div>
          <ScrollArea
            h={550}
            onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
            className="scroll-table"
            ref={scrollAreaRef}
          >
            <Table
              highlightOnHover
              verticalSpacing="xs"
              className="inactive-clients-table"
            >
              <Table.Thead
                className={cx(classes.header, {
                  [classes.scrolled]: scrolled,
                })}
              >
                <Table.Tr>
                  <Table.Th>Client Name</Table.Th>
                  <Table.Th className="hide-on-small-screens">
                    Account Created
                  </Table.Th>
                  <Table.Th>Actions Needed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {getInactiveClients(clientsToDisplay[value])}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Card>
      </div>
    </>
  );
};

export default InactiveClients;
